import { Denounce } from "@/models/denounce.model";
import { QueryParams } from "@/models/query-params.model";
import { CommentRest } from "@/services/rest/comment.rest";
import { CoursesRest } from "@/services/rest/courses.rest";
import { TopicsRest } from "@/services/rest/topics.rest";
import { getUserFromStorage } from "@/utils/user-store.util";
import {
  Subject,
  Observable,
  BehaviorSubject,
  take,
  catchError,
  of,
} from "rxjs";

export class ForumService {
  constructor(
    private _topicsRest = new TopicsRest(),
    private _coursesRest = new CoursesRest(),
    private _commentsRest = new CommentRest()
  ) {}

  private course$: Subject<any> = new Subject<any>();
  private courses$: BehaviorSubject<any> = new BehaviorSubject<any>([]);
  private topics$: Subject<any> = new Subject<any>();
  private topic$: Subject<any> = new Subject<any>();
  private comment$: Subject<any> = new Subject<any>();
  private comments$: BehaviorSubject<any> = new BehaviorSubject<any>([]);
  private denounce$: Subject<any> = new Subject<any>();
  private denounces$: Subject<any> = new Subject<any>();

  course: Observable<any> = this.course$.asObservable();
  courses: Observable<any> = this.courses$.asObservable();
  topics: Observable<any> = this.topics$.asObservable();
  topic: Observable<any> = this.topic$.asObservable();
  comments: Observable<any> = this.comments$.asObservable();
  comment: Observable<any> = this.comment$.asObservable();
  denounce: Observable<any> = this.denounce$.asObservable();
  denounces: Observable<any> = this.denounces$.asObservable();

  getAllTopics(params?: QueryParams): void {
    this._topicsRest
      .getAllTopics(params)
      .pipe()
      .subscribe({
        next: (response) => {
          this.topics$.next(response);
        },
        error: (error) => {
          this.topics$.error(error);
          return error;
        },
      });
  }

  getTopicById(topicId: string) {
    this._commentsRest
      .getAllComments({} as QueryParams, topicId)
      .pipe(
        take(1),
        catchError(() => of(null))
      )
      .subscribe({
        next: (response) => {
          this.topic$.next(response);
        },
        error: (error) => {
          this.topic$.error(error);
          return error;
        },
      });
  }
  createTopic(topicData: FormData): void {
    this._topicsRest
      .createTopic(topicData)
      .pipe(
        take(1),
        catchError(() => of(null))
      )
      .subscribe({
        next: (response) => {
          this.topics$.next(response);
        },
        error: (error) => {
          this.topics$.error(error);
          return error;
        },
      });
  }
  deleteTopic(topicId: string): void {
    this._topicsRest
      .deleteTopic(topicId)
      .pipe(
        take(1),
        catchError(() => of(null))
      )
      .subscribe({
        next: (response) => {
          this.topic$.next(response);
        },
        error: (error) => {
          this.topic$.error(error);
          return error;
        },
      });
  }

  createComment(topicId: string, commentData: FormData): void {
    this._commentsRest
      .createComment(topicId, commentData)
      .pipe(
        take(1),
        catchError(() => of(null))
      )
      .subscribe({
        next: (response) => {
          this.comment$.next(response);
        },
        error: (error) => {
          this.comment$.error(error);
          return error;
        },
      });
  }
  getCourseById(courseId: string, baseUrl?: string): void {
    this._coursesRest
      .getCoursesById(courseId, baseUrl!)
      .pipe(
        take(1),
        catchError(() => of(null))
      )
      .subscribe({
        next: (response) => {
          this.course$.next(response);
        },
        error: (error) => {
          this.course$.error(error);
          return error;
        },
      });
  }
  getCourses(params: QueryParams): void {
    if (getUserFromStorage().role !== "ADMINISTRATOR") {
      this._coursesRest
        .getCourses(params)
        .pipe()
        .subscribe({
          next: (response) => {
            this.courses$.next(response);
          },
          error: (error) => {
            this.courses$.error(error);
            return error;
          },
        });
    } else {
      this._coursesRest
        .getCoursesAdmin(params)
        .pipe(take(1))
        .subscribe((response) => {
          this.courses$.next(response);
        });
    }
  }

  updateTopic(topicId: string, topicData: FormData): void {
    this._topicsRest
      .updateTopic(topicId, topicData)
      .pipe(
        take(1),
        catchError(() => of(null))
      )
      .subscribe({
        next: (response) => {
          this.topic$.next(response);
        },
        error: (error) => {
          this.topic$.error(error);
          return error;
        },
      });
  }
  likeTopic(id: string): void {
    this._topicsRest
      .likeTopic(id)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.topic$.next(response);
        },
        error: (error) => {
          this.topic$.error(error);
          return error;
        },
      });
  }
  getAllCommentsByTopic(params?: QueryParams, topicId?: string): void {
    this._commentsRest
      .getAllComments(params, topicId)
      .pipe(
        take(1),
        catchError(() => of(null))
      )
      .subscribe({
        next: (response) => {
          this.comments$.next(response);
        },
        error: (error) => {
          this.comments$.error(error);
          return error;
        },
      });
  }
  likeComment(topicId: string, commentId: string): void {
    this._commentsRest
      .likeComment(topicId, commentId)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.comment$.next(response);
        },
        error: (error) => {
          this.comment$.error(error);
          return error;
        },
      });
  }
  deleteCommentByTopic(topicId: string, commentId: string): void {
    this._commentsRest
      .deleteComment(topicId, commentId)
      .pipe(
        take(1),
        catchError(() => of(null))
      )
      .subscribe({
        next: (response) => {
          this.comment$.next(response);
        },
        error: (error) => {
          this.comment$.error(error);
          return error;
        },
      });
  }
  getCommentById(topicId: string, commentId: string): void {
    this._commentsRest
      .getCommentById(topicId, commentId)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.comment$.next(response);
        },
        error: (error) => {
          this.comment$.error(error);
          return error;
        },
      });
  }
  editComment(
    topicId: string,
    commentId: string,
    commentUpdated: FormData
  ): void {
    this._commentsRest
      .editComment(topicId, commentId, commentUpdated)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.comment$.next(response);
        },
        error: (error) => {
          this.comment$.error(error);
          return error;
        },
      });
  }

  denounceTopic(id: string, denounce: Denounce): void {
    this._topicsRest
      .denunceTopic(id, denounce)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.topic$.next(response);
        },
      });
  }
  denounceComment(topicId: string, commentId: string, body: Denounce): void {
    this._commentsRest
      .denounceComment(topicId, commentId, body)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.comment$.next(response);
        },
      });
  }

  getAllDenouncesComment(params: QueryParams): void {
    this._commentsRest
      .getAllDenouncesComment(params)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.denounces$.next(response);
        },
      });
  }

  getAllDenouncesTopic(params: QueryParams): void {
    this._topicsRest
      .getAllDenouncesTopic(params)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.denounces$.next(response);
        },
      });
  }

  editCommentDenounce(commentId: string, denounces: Array<Denounce>): void {
    this._commentsRest
      .updateCommentDenounce(commentId, denounces)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.denounce$.next(response);
        },
        error: (error) => {
          this.denounce$.error(error);
          return error;
        },
      });
  }

  editTopicDenounce(topicId: string, denounces: Array<Denounce>): void {
    this._topicsRest
      .updateTopicDenounce(topicId, denounces)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.denounce$.next(response);
        },
        error: (error) => {
          this.denounce$.error(error);
          return error;
        },
      });
  }
}
